import { useCallback, useMemo, useState } from 'react';

import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebounce } from '@/hooks/useDebounce';
import { operationsClient, apiPost } from '@/lib/api';
import { schedulerEndpoints, schedulingPrefix } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import {
  preflightRequestKey,
  requiresAcknowledgement as conflictsRequireAcknowledgement,
  type ScheduleConflict,
} from '@/lib/scheduler/scheduleConflicts';

export interface SchedulePreflightInput {
  /** Lead first; empty → no check. */
  technicianIds: readonly string[];
  /** UTC ISO instant (ending in Z). */
  startAt?: string | null;
  endAt?: string | null;
  serviceTypeId?: string | null;
  /** Server derives required skills / crew size from the job type. */
  jobTypeId?: string | null;
  /** Server derives skills, ZIP and crew from the job. */
  jobId?: string | null;
  /** The visit being edited — its own slot is not a conflict. */
  excludeAppointmentId?: string | null;
  zipCode?: string | null;
  address?: string | null;
  businessUnitId?: string | null;
  promisedStartAt?: string | null;
  promisedEndAt?: string | null;
  /** Default true. False parks the hook (status 'idle'). */
  enabled?: boolean;
  /** Default 400ms. */
  debounceMs?: number;
}

export type SchedulePreflightStatus = 'idle' | 'checking' | 'ready' | 'error';

export interface PreflightState {
  status: SchedulePreflightStatus;
  conflicts: ScheduleConflict[];
  hasBlocking: boolean;
  /** Any BLOCK or WARN present — the save needs the user's acknowledgement. */
  requiresAcknowledgement: boolean;
  /** The user ticked "schedule anyway" for THIS tech/time (resets when either changes). */
  acknowledged: boolean;
  setAcknowledged: (next: boolean) => void;
  /** Value to send as `overrideConflicts` on the write. */
  overrideConflicts: boolean;
  /** False only when an acknowledgement is required and not given. True while checking. */
  canSubmit: boolean;
  routeIssue?: string;
  routeVerified?: boolean;
  /** Stable identity of the current request (tech ids sorted + time + context). */
  requestKey: string;
  refetch: () => void;
}

interface ConflictCheckBody {
  technicianId?: string;
  technicianIds: string[];
  startAt: string;
  endAt: string;
  serviceTypeId?: string;
  jobTypeId?: string;
  jobId?: string;
  excludeAppointmentId?: string;
  zipCode?: string;
  address?: string;
  businessUnitId?: string;
  promisedStartAt?: string;
  promisedEndAt?: string;
}

interface ConflictCheckResponse {
  conflicts: ScheduleConflict[];
  hasBlocking: boolean;
  territoryId: string | null;
  routeCheck?: { feasible: boolean; message?: string };
}

const EMPTY_CONFLICTS: ScheduleConflict[] = [];

/**
 * Live, debounced conflict preflight for a scheduling form: as the user picks
 * a technician/time, POST /conflicts/check runs (400ms after the last change)
 * and the banner shows the Busy / Out of area / Missing skill issues. The user
 * acknowledges them inline; `overrideConflicts` is what the form then sends.
 *
 * The acknowledgement is stored as "the request key the user acknowledged", so
 * changing the tech or the time silently resets it — nobody acknowledges
 * conflicts they haven't seen.
 */
export function useSchedulePreflight(input: SchedulePreflightInput): PreflightState {
  const {
    technicianIds,
    startAt,
    endAt,
    serviceTypeId,
    jobTypeId,
    jobId,
    excludeAppointmentId,
    zipCode,
    address,
    businessUnitId,
    promisedStartAt,
    promisedEndAt,
    enabled = true,
    debounceMs = 400,
  } = input;

  // One stable snapshot per distinct set of inputs (the caller may pass a fresh
  // array each render; the joined key keeps the memo honest).
  const technicianIdsKey = technicianIds.filter(Boolean).join(',');
  const snapshot = useMemo(
    () => ({
      technicianIds: technicianIdsKey ? technicianIdsKey.split(',') : [],
      startAt: startAt ?? null,
      endAt: endAt ?? null,
      serviceTypeId: serviceTypeId ?? null,
      jobTypeId: jobTypeId ?? null,
      jobId: jobId ?? null,
      excludeAppointmentId: excludeAppointmentId ?? null,
      zipCode: zipCode ?? null,
      address: address ?? null,
      businessUnitId: businessUnitId ?? null,
      promisedStartAt: promisedStartAt ?? null,
      promisedEndAt: promisedEndAt ?? null,
    }),
    [
      technicianIdsKey,
      startAt,
      endAt,
      serviceTypeId,
      jobTypeId,
      jobId,
      excludeAppointmentId,
      zipCode,
      address,
      businessUnitId,
      promisedStartAt,
      promisedEndAt,
    ],
  );
  const debounced = useDebounce(snapshot, debounceMs);
  const immediateKey = preflightRequestKey(snapshot);
  const requestKey = preflightRequestKey(debounced);
  const settling = requestKey !== immediateKey;

  const isComplete = (value: typeof snapshot) =>
    value.technicianIds.length > 0 && Boolean(value.startAt) && Boolean(value.endAt);
  // `active` — the user has picked enough to check (drives idle vs checking);
  // `queryEnabled` — the debounced request is runnable.
  const active = enabled !== false && isComplete(snapshot);
  const queryEnabled = enabled !== false && isComplete(debounced);

  const query = useApiQuery<ConflictCheckResponse>({
    queryKey: schedulerKeys.conflictPreflight(requestKey),
    queryFn: ({ signal }) => {
      const body: ConflictCheckBody = {
        technicianId: debounced.technicianIds[0],
        technicianIds: debounced.technicianIds,
        startAt: debounced.startAt!,
        endAt: debounced.endAt!,
        ...(debounced.address && schedulingPrefix() === '/v2'
          ? { address: debounced.address }
          : {}),
        ...(debounced.businessUnitId && schedulingPrefix() === '/v2'
          ? { businessUnitId: debounced.businessUnitId }
          : {}),
        ...(schedulingPrefix() === '/v2' && debounced.promisedStartAt && debounced.promisedEndAt
          ? { promisedStartAt: debounced.promisedStartAt, promisedEndAt: debounced.promisedEndAt }
          : {}),
        ...(debounced.serviceTypeId ? { serviceTypeId: debounced.serviceTypeId } : {}),
        ...(debounced.jobTypeId ? { jobTypeId: debounced.jobTypeId } : {}),
        ...(debounced.jobId ? { jobId: debounced.jobId } : {}),
        ...(debounced.excludeAppointmentId
          ? { excludeAppointmentId: debounced.excludeAppointmentId }
          : {}),
        ...(debounced.zipCode ? { zipCode: debounced.zipCode } : {}),
      };
      return apiPost<ConflictCheckResponse, ConflictCheckBody>(
        operationsClient,
        schedulerEndpoints.conflictsCheck(),
        body,
        { signal },
      );
    },
    enabled: queryEnabled,
    staleTime: 15_000,
    showErrorToast: false,
    retry: false,
  });

  // The user acknowledges ONE request; any change in tech/time produces a new key
  // and the acknowledgement no longer applies.
  const [ackKey, setAckKey] = useState<string | null>(null);
  const acknowledged = ackKey != null && ackKey === requestKey && !settling;
  const setAcknowledged = useCallback(
    (next: boolean) => setAckKey(next ? requestKey : null),
    [requestKey],
  );

  // A background refetch of the SAME key keeps 'ready' (no banner flicker); only
  // a new key (settling / first fetch) reads as 'checking'.
  let status: SchedulePreflightStatus;
  if (!active) status = 'idle';
  else if (settling || !queryEnabled || query.isPending) status = 'checking';
  else if (query.isError) status = 'error';
  else status = 'ready';
  const conflicts = status === 'ready' && query.data ? query.data.conflicts : EMPTY_CONFLICTS;

  const requiresAcknowledgement = status === 'ready' && conflictsRequireAcknowledgement(conflicts);
  const hasBlocking = status === 'ready' && Boolean(query.data?.hasBlocking);
  const windowAtCapacity = conflicts.some(
    (conflict) =>
      conflict.type === 'WINDOW_CAPACITY_EXCEEDED' && conflict.details?.quotaExceeded === true,
  );
  const overrideConflicts = !windowAtCapacity && requiresAcknowledgement && acknowledged;
  const routeIssue =
    status === 'ready' && query.data?.routeCheck?.feasible === false
      ? (query.data.routeCheck.message ?? 'No feasible route at this time.')
      : undefined;
  const routeVerified = status === 'ready' && query.data?.routeCheck?.feasible === true;
  const canSubmit = !windowAtCapacity && !routeIssue && (!requiresAcknowledgement || acknowledged);

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  return {
    status,
    conflicts,
    hasBlocking,
    requiresAcknowledgement,
    acknowledged,
    setAcknowledged,
    overrideConflicts,
    canSubmit,
    routeIssue,
    routeVerified,
    requestKey,
    refetch,
  };
}
